export type RiskLevel = 'low' | 'moderate' | 'high' | 'critical'

export type CategoryId =
    | 'c1-recordkeeping'
    | 'c2-leak-rate'
    | 'c3-technician'
    | 'c4-audit-prep'
    | 'c5-operational'

export type Urgency = 'critical' | 'high' | 'moderate' | 'low'
export type ChecklistAnswer = 'yes' | 'no' | 'na'

export interface ChecklistQuestion {
    id:string;
    category:CategoryId;
    text:string;
    urgency:Urgency;
    actionItem:string;
}

export interface CategoryDef {
    id:CategoryId;
    label:string;
    weight:number;
    questionIds:string[];
}

export interface ActionItem {
    urgency:Urgency;
    text:string;
    questionId:string;
}

export interface CategoryScore {
    id:CategoryId;
    label:string;
    score:number;
    weight:number;
    weightedScore:number;
    failCount:number;
    applicableCount:number;
}

export interface AuditChecklistResult {
    totalScore:number;
    riskLevel:RiskLevel;
    categoryScores:CategoryScore[];
    actionItems:ActionItem[];
}

export const CHECKLIST_QUESTIONS:ChecklistQuestion[] = [
    // Category 1: Refrigerant Recordkeeping
    {
        id: 'q1',
        category: 'c1-recordkeeping',
        text: 'Do you maintain service records for each appliance containing ≥50 lbs of refrigerant?',
        urgency: 'high',
        actionItem:
            'Implement a service recordkeeping system for all appliances ≥50 lbs. ' +
            'Records must be retained for 3 years per 40 CFR 82.166.',
    },
    {
        id: 'q2',
        category: 'c1-recordkeeping',
        text:
            'Do service records include date of service, refrigerant type, ' +
            'quantity added/recovered, and technician ID?',
        urgency: 'high',
        actionItem:
            'Update service record forms to capture all required fields: date, ' +
            'refrigerant type, quantity added and recovered, and technician ' +
            'certification number.',
    },
    {
        id: 'q3',
        category: 'c1-recordkeeping',
        text: 'Are service records retained for at least 3 years?',
        urgency: 'high',
        actionItem:
            'Establish a 3-year record retention policy and archive older records ' +
            'before disposal.',
    },
    {
        id: 'q4',
        category: 'c1-recordkeeping',
        text: 'Do you maintain a current inventory of all refrigerant-containing appliances?',
        urgency: 'moderate',
        actionItem:
            'Create and maintain an equipment register listing each appliance, ' +
            'location, refrigerant type, and full charge weight.',
    },
    {
        id: 'q5',
        category: 'c1-recordkeeping',
        text: 'Are refrigerant purchase and recovery transaction records maintained?',
        urgency: 'high',
        actionItem:
            'Begin tracking all refrigerant purchases and recovery amounts per ' +
            'service event to establish a complete audit trail.',
    },
    {
        id: 'q6',
        category: 'c1-recordkeeping',
        text: 'Are refrigerant cylinder transfers and disposals documented?',
        urgency: 'moderate',
        actionItem:
            'Document all cylinder-to-cylinder transfers and disposals, ' +
            'including dates and quantities.',
    },
    // Category 2: Leak Rate Compliance
    {
        id: 'q7',
        category: 'c2-leak-rate',
        text: 'Do you calculate annualized leak rates for appliances ≥50 lbs whenever refrigerant is added?',
        urgency: 'high',
        actionItem:
            'Implement annualized leak rate calculations for every service event ' +
            'where refrigerant is added. Use the Leak Rate Calculator for ' +
            '(added / full charge) × (365 / days) × 100.',
    },
    {
        id: 'q8',
        category: 'c2-leak-rate',
        text: 'When a commercial refrigeration appliance exceeds the 20% leak rate, is repair initiated within 30 days?',
        urgency: 'critical',
        actionItem:
            'Initiate immediate repair for all commercial refrigeration appliances ' +
            'with leak rates exceeding 20%. The 30-day repair deadline is a hard ' +
            'EPA requirement under 40 CFR 82.156.',
    },
    {
        id: 'q9',
        category: 'c2-leak-rate',
        text: 'When an industrial process refrigeration appliance exceeds the 30% leak rate, is repair initiated within 30 days?',
        urgency: 'critical',
        actionItem:
            'Initiate immediate repair for all industrial process refrigeration ' +
            'appliances with leak rates exceeding 30%. Document the start date ' +
            'and progress of all repairs.',
    },
    {
        id: 'q10',
        category: 'c2-leak-rate',
        text: 'When a comfort cooling appliance exceeds the 10% leak rate, is repair initiated within 30 days?',
        urgency: 'critical',
        actionItem:
            'Initiate immediate repair for all comfort cooling appliances with ' +
            'leak rates exceeding 10%. The repair window begins on the date the ' +
            'leak is identified.',
    },
    {
        id: 'q11',
        category: 'c2-leak-rate',
        text: 'Is the date of the last refrigerant addition recorded for every appliance?',
        urgency: 'high',
        actionItem:
            'Record the date of every refrigerant addition in the appliance ' +
            'service record. This date is required to calculate annualized leak ' +
            'rates and document repair timelines.',
    },
    {
        id: 'q12',
        category: 'c2-leak-rate',
        text: 'Are appliances receiving refrigerant three or more times in 12 months flagged for leak inspection?',
        urgency: 'moderate',
        actionItem:
            'Flag any appliance receiving refrigerant three or more times within ' +
            '12 months for a thorough leak inspection. Frequent additions indicate ' +
            'an unidentified or unrepaired leak.',
    },
    // Category 3: Technician Compliance
    {
        id: 'q13',
        category: 'c3-technician',
        text: 'Do all technicians performing refrigerant work hold valid EPA Section 608 certification?',
        urgency: 'critical',
        actionItem:
            'Immediately verify certification status of all technicians handling ' +
            'refrigerants. Uncertified technicians must not perform refrigerant ' +
            'work. Civil penalties can reach $44,539 per day per violation.',
    },
    {
        id: 'q14',
        category: 'c3-technician',
        text: 'Are technician EPA 608 certification records (type and certificate number) maintained on file?',
        urgency: 'high',
        actionItem:
            'Collect and file a copy of each technician\'s EPA Section 608 ' +
            'certification. Records must be producible upon EPA inspector request.',
    },
    {
        id: 'q15',
        category: 'c3-technician',
        text: 'Are all refrigerant recovery/recycling machines certified by an EPA-approved testing organization?',
        urgency: 'high',
        actionItem:
            'Verify that all recovery and recycling machines are certified by an ' +
            'EPA-approved organization (e.g., UL, AHRI). Remove uncertified ' +
            'equipment from service immediately.',
    },
    {
        id: 'q16',
        category: 'c3-technician',
        text: 'Have technicians received documented training on proper recovery procedures and EPA 608 requirements?',
        urgency: 'moderate',
        actionItem:
            'Schedule and document refresher training on EPA Section 608 ' +
            'requirements, proper recovery procedures, and recordkeeping ' +
            'obligations for all relevant staff.',
    },
    {
        id: 'q17',
        category: 'c3-technician',
        text: 'Is technician certification verified before assigning individuals to refrigerant work?',
        urgency: 'high',
        actionItem:
            'Establish a pre-assignment verification step: confirm active ' +
            'certification status before scheduling any technician for ' +
            'refrigerant work.',
    },
    // Category 4: Audit Preparedness
    {
        id: 'q18',
        category: 'c4-audit-prep',
        text: 'Can all required service records be produced within 24 hours of an EPA request?',
        urgency: 'high',
        actionItem:
            'Organize service records by appliance ID and year so they can be ' +
            'retrieved immediately. Conduct a mock records-request drill to ' +
            'verify retrieval speed.',
    },
    {
        id: 'q19',
        category: 'c4-audit-prep',
        text: 'Are records organized and indexed by appliance ID, location, or date?',
        urgency: 'moderate',
        actionItem:
            'Implement a filing or indexing system for service records. Digital ' +
            'records should be searchable by appliance ID, site location, and ' +
            'date of service.',
    },
    {
        id: 'q20',
        category: 'c4-audit-prep',
        text: 'Is there a designated compliance coordinator for EPA Section 608 matters?',
        urgency: 'moderate',
        actionItem:
            'Assign a specific individual as the EPA 608 compliance coordinator ' +
            'responsible for recordkeeping oversight and inspector liaison.',
    },
    {
        id: 'q21',
        category: 'c4-audit-prep',
        text: 'Has an internal compliance review been conducted within the past 12 months?',
        urgency: 'moderate',
        actionItem:
            'Schedule and complete an internal EPA Section 608 compliance review. ' +
            'Document all findings and corrective actions taken.',
    },
    {
        id: 'q22',
        category: 'c4-audit-prep',
        text: 'Do written procedures exist for refrigerant handling, recovery, and recordkeeping?',
        urgency: 'moderate',
        actionItem:
            'Develop and distribute written standard operating procedures for ' +
            'refrigerant handling, recovery, and service recordkeeping.',
    },
    {
        id: 'q23',
        category: 'c4-audit-prep',
        text: 'Is all refrigerant-containing equipment accessible and clearly labeled for inspection?',
        urgency: 'low',
        actionItem:
            'Ensure all refrigerant-containing equipment and associated records ' +
            'are accessible to EPA inspectors during normal business hours and ' +
            'that equipment is clearly identified.',
    },
    // Category 5: Operational Risk
    {
        id: 'q24',
        category: 'c5-operational',
        text: 'Is refrigerant always recovered from appliances before disposal or major component service?',
        urgency: 'critical',
        actionItem:
            'Enforce a zero-venting policy. Refrigerant must be recovered before ' +
            'any appliance is disposed of or opened for major service. Intentional ' +
            'venting carries substantial civil and criminal penalties.',
    },
    {
        id: 'q25',
        category: 'c5-operational',
        text: 'Is recovered refrigerant properly reclaimed, recycled, or disposed of per EPA regulations?',
        urgency: 'high',
        actionItem:
            'Ensure recovered refrigerant is sent to a certified reclaimer or ' +
            'properly disposed of. Retain reclaimer receipts as part of your ' +
            'refrigerant transaction records.',
    },
    {
        id: 'q26',
        category: 'c5-operational',
        text: 'Are refrigerant cylinders properly labeled, stored safely, and tracked in inventory?',
        urgency: 'moderate',
        actionItem:
            'Label all refrigerant cylinders with refrigerant type and quantity. ' +
            'Store cylinders safely and maintain a current cylinder inventory log.',
    },
    {
        id: 'q27',
        category: 'c5-operational',
        text: 'Has the EPA phase-out or phasedown timeline been reviewed for all refrigerants currently in use?',
        urgency: 'low',
        actionItem:
            'Review the AIM Act and 40 CFR Part 84 phasedown schedule for all ' +
            'refrigerants in use. Plan transitions away from high-GWP and ' +
            'phased-out refrigerants.',
    },
    {
        id: 'q28',
        category: 'c5-operational',
        text: 'Are technicians and service staff informed of regulatory changes affecting the refrigerants they handle?',
        urgency: 'low',
        actionItem:
            'Establish a process to communicate regulatory changes to relevant ' +
            'staff, including updates on phaseout schedules and new EPA ' +
            'requirements under 40 CFR Part 82 and Part 84.',
    },
]

export const CHECKLIST_CATEGORIES:CategoryDef[] = [
    {
        id: 'c1-recordkeeping',
        label: 'Refrigerant Recordkeeping',
        weight: 25,
        questionIds: ['q1', 'q2', 'q3', 'q4', 'q5', 'q6'],
    },
    {
        id: 'c2-leak-rate',
        label: 'Leak Rate Compliance',
        weight: 30,
        questionIds: ['q7', 'q8', 'q9', 'q10', 'q11', 'q12'],
    },
    {
        id: 'c3-technician',
        label: 'Technician Compliance',
        weight: 20,
        questionIds: ['q13', 'q14', 'q15', 'q16', 'q17'],
    },
    {
        id: 'c4-audit-prep',
        label: 'Audit Preparedness',
        weight: 15,
        questionIds: ['q18', 'q19', 'q20', 'q21', 'q22', 'q23'],
    },
    {
        id: 'c5-operational',
        label: 'Operational Risk',
        weight: 10,
        questionIds: ['q24', 'q25', 'q26', 'q27', 'q28'],
    },
]

const URGENCY_ORDER:Record<Urgency, number> = {
    critical: 0,
    high: 1,
    moderate: 2,
    low: 3,
}

function riskFromScore (score:number):RiskLevel {
    if (score >= 75) return 'critical'
    if (score >= 50) return 'high'
    if (score >= 25) return 'moderate'
    return 'low'
}

export function calcAuditChecklist (
    answers:Record<string, ChecklistAnswer>
):AuditChecklistResult {
    const questionMap = new Map(
        CHECKLIST_QUESTIONS.map(q => [q.id, q])
    )

    const categoryScores:CategoryScore[] = CHECKLIST_CATEGORIES.map(cat => {
        const applicable = cat.questionIds.filter(
            id => (answers[id] ?? 'na') !== 'na'
        )
        const failed = applicable.filter(
            id => answers[id] === 'no'
        )
        const applicableCount = applicable.length
        const failCount = failed.length
        const score = applicableCount > 0 ?
            (failCount / applicableCount) * 100 :
            0
        const weightedScore = score * cat.weight / 100

        return {
            id: cat.id,
            label: cat.label,
            score,
            weight: cat.weight,
            weightedScore,
            failCount,
            applicableCount,
        }
    })

    const totalScore = categoryScores.reduce(
        (s, c) => s + c.weightedScore, 0
    )
    const riskLevel = riskFromScore(totalScore)

    const actionItems:ActionItem[] = CHECKLIST_QUESTIONS
        .filter(q => answers[q.id] === 'no')
        .map(q => ({
            urgency: q.urgency,
            text: questionMap.get(q.id)!.actionItem,
            questionId: q.id,
        }))
        .sort(
            (a, b) => URGENCY_ORDER[a.urgency] - URGENCY_ORDER[b.urgency]
        )

    return {
        totalScore,
        riskLevel,
        categoryScores,
        actionItems,
    }
}
